/**
 * CDB-TQS API — Clients, Documents, Jobs and Transcription
 */

import { parseVersionError, type DocumentVersionState } from './documentVersions'
import { logApiCall, logApiResponse, logError } from "@/lib/logger";
import type {
  CdbTqsDocumentListResponse,
  DocumentMediaType,
  DocumentSortDirection,
  DocumentSortKey,
  DocumentStatus,
} from "@/types/cdb-tqs-documents";
import {
  CDB_TQS_API_BASE_URL,
  fetchWithLogging,
  getAuthHeaders,
  getMimeTypeFromFilename,
} from "./http";
import {
  parseResumableUploadInfo,
  parseRetryAfterMs,
  parseVideoIngestFormats,
  readBlobCapped,
  type ResumableUploadInfo,
  type VideoIngestFormats,
} from "./video";

/** Existierendes Dokument aus dem 409-Detail des Upload-Endpoints. */
export interface ExistingDocumentInfo {
  document_id: string
  title: string
  filename: string
  knowledge_area: string
  status: string
  created_at: string | null
}

/**
 * Upload-409: identischer Datei-Inhalt (SHA-256) existiert bereits im System —
 * der Check ist global über alle Knowledge Areas. Trägt das existierende
 * Dokument aus dem Backend-Detail, damit die UI sagen kann, wo es liegt (#777).
 */
export class DuplicateDocumentError extends Error {
  readonly existingDocument: ExistingDocumentInfo | null

  constructor(message: string, existingDocument: ExistingDocumentInfo | null) {
    super(message)
    this.name = 'DuplicateDocumentError'
    this.existingDocument = existingDocument
  }
}

/**
 * Upload-429: serverseitiges Upload-Limit erreicht (TQS: fixes Fenster,
 * z. B. 50 Dateien / 10 Minuten pro User — ohne Retry-After-Header).
 * Der Aufrufer kann warten und denselben Upload erneut versuchen (#411).
 */
export class UploadRateLimitError extends Error {
  /** From the server's `Retry-After`, when it sent one. */
  readonly retryAfterMs?: number

  constructor(message: string, retryAfterMs?: number) {
    super(message)
    this.name = 'UploadRateLimitError'
    this.retryAfterMs = retryAfterMs
  }
}

/**
 * Upload-415 body (cdb_tqs#1515): the Knowledge Area cannot turn this file
 * type into text. `message` is German and only a fallback — clients show a
 * reason by `extension` in the user's language.
 */
export interface UnsupportedFormatDetail {
  code: 'unsupported_format'
  message: string
  /** Lower-case with dot (".heic"); null for a file without extension. */
  extension: string | null
  allowed: string[]
}

/** The 415 detail, or null when `data` is not one (older backend, proxy page). */
export function parseUnsupportedFormatDetail(data: unknown): UnsupportedFormatDetail | null {
  const detail = data && typeof data === 'object' && 'detail' in data
    ? (data as { detail: unknown }).detail
    : data
  if (!detail || typeof detail !== 'object') return null
  const raw = detail as Record<string, unknown>
  if (raw.code !== 'unsupported_format') return null
  return {
    code: 'unsupported_format',
    message: typeof raw.message === 'string' ? raw.message : '',
    extension: typeof raw.extension === 'string' && raw.extension ? raw.extension.toLowerCase() : null,
    allowed: Array.isArray(raw.allowed) ? raw.allowed.filter((e): e is string => typeof e === 'string') : [],
  }
}

/** Upload-415: final, a retry gets the same answer. */
export class UnsupportedFormatError extends Error {
  readonly detail: UnsupportedFormatDetail

  constructor(detail: UnsupportedFormatDetail) {
    super(detail.message || `Unsupported format ${detail.extension ?? ''}`.trim())
    this.name = 'UnsupportedFormatError'
    this.detail = detail
  }
}

/**
 * Upload-404 `drive_file_not_found` (cdb_tqs#1515): the `drive_file_id` sent
 * with the upload is not one of the caller's own Drive files. Nothing was
 * stored or counted.
 */
export class DriveFileNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DriveFileNotFoundError'
  }
}

/** Links an upload to the Drive file it came from (cdb_tqs#1515). */
export interface UploadDriveLink {
  /** Personal-file id (any version of the chain) or chat-attachment id. */
  fileId: string
  /** Version that is synced (>= 1); omitted for attachments. */
  version?: number
}

/** 202 of `POST /documents/upload`. A failed duplicate answers with its own id. */
export interface UploadJobResponse {
  job_id?: string
  document_id?: string
  status?: string
  message?: string
}

/** `GET /documents/formats` (cdb_tqs#1515). */
export interface IngestFormats {
  /** Lower-case with dot, e.g. ".pdf". */
  extensions: string[]
  images_enabled: boolean
  max_file_size_mb: number | null
  /** Formats a client must convert first (".heic", ".heif"). */
  convert_before_upload: string[]
  /** Formats that go through the Excel import instead. */
  excel_import: string[]
  /** Video plan (TQS-1): absent on a backend without video support. */
  video?: VideoIngestFormats
  /** Video plan (TQS-2): block upload offered; absent while its flag is off. */
  resumable?: ResumableUploadInfo
}

function extensionList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((e): e is string => typeof e === 'string' && e.length > 0)
    .map((e) => (e.startsWith('.') ? e : `.${e}`).toLowerCase())
}

/** The formats payload, or null when it is not one (e.g. a backend without the route). */
export function parseIngestFormats(data: unknown): IngestFormats | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  if (!Array.isArray(raw.extensions)) return null
  return {
    extensions: extensionList(raw.extensions),
    images_enabled: raw.images_enabled === true,
    max_file_size_mb: typeof raw.max_file_size_mb === 'number' ? raw.max_file_size_mb : null,
    convert_before_upload: extensionList(raw.convert_before_upload),
    excel_import: extensionList(raw.excel_import),
    video: parseVideoIngestFormats(raw),
    resumable: parseResumableUploadInfo(raw),
  }
}

/** Largest page TQS serves for the document list (cdb_tqs#1382). */
export const DOCUMENT_PAGE_SIZE = 100;

export const documentsApi = {
  /**
   * Get all clients
   * @returns List of clients with user counts and bucket names
   */
  async getClients(): Promise<any[]> {
    // Trailing slash required to avoid 307 redirect (proxies may rewrite the scheme on redirects)
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/clients/`, {
      method: 'GET',
    })
    return response.data as any[]
  },

  /**
   * Create a new client (automatically creates storage bucket)
   * @param data - Client creation data
   * @returns Created client
   */
  async createClient(data: { name: string }): Promise<any> {
    // Trailing slash required to avoid 307 redirect which loses Authorization header
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/clients/`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data
  },

  /**
   * Update a client
   * @param id - Client ID
   * @param data - Updated client data
   * @returns Updated client
   */
  async updateClient(id: string, data: { name: string }): Promise<any> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/clients/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    return response.data
  },

  /**
   * Delete a client and optionally its storage bucket
   * @param id - Client ID
   * @param deleteBucket - If true, also delete the storage bucket (default: false)
   */
  async deleteClient(id: string, deleteBucket: boolean = false): Promise<void> {
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/clients/${id}?delete_bucket=${deleteBucket}`, {
      method: 'DELETE',
    })
  },

  /**
   * Get all documents from CDB-TQS backend
   * @param knowledgeArea - Optional filter by knowledge area name (server-side)
   * @param limit - Maximum number of documents to return (default: 100)
   * @param offset - Number of documents to skip (default: 0)
   * @param statusFilter - Optional filter by document status, or several (cdb_webos#1645)
   * @param search - Optional search term to filter by title or filename (server-side)
   * @param sortBy - Optional column to order by (server-side; default: created_at)
   * @param sortDir - Optional order direction (server-side; default: desc)
   * @returns List of documents
   */
  async getDocuments(
    knowledgeArea?: string,
    limit: number = 100,
    offset: number = 0,
    statusFilter?: DocumentStatus | readonly DocumentStatus[],
    search?: string,
    sortBy?: DocumentSortKey,
    sortDir?: DocumentSortDirection,
    uploadedByMe?: boolean,
    mediaType?: DocumentMediaType,
    /** cdb_tqs#1974 1.4: only this version state; every state when omitted. */
    versionState?: DocumentVersionState
  ): Promise<CdbTqsDocumentListResponse> {
    const params: Record<string, string> = {
      limit: String(limit),
      offset: String(offset),
    };

    // Several statuses go comma-separated; TQS binds them as one array.
    const statuses = typeof statusFilter === "string" ? [statusFilter] : statusFilter ?? [];
    if (statuses.length > 0) {
      params.status_filter = statuses.join(",");
    }

    // Server-side knowledge area filtering (backend supports this now)
    if (knowledgeArea) {
      params.knowledge_area = knowledgeArea;
    }

    if (search) {
      params.search = search;
    }

    // Sorting is server-side because the list is paged — sorting only the page
    // in hand would order a slice and label it the whole list.
    if (sortBy) {
      params.sort_by = sortBy;
    }

    if (sortDir) {
      params.sort_dir = sortDir;
    }

    // Server-side for the same reason as the sort: a filter over the page in
    // hand would hide own uploads that sit on a later page (#1106).
    if (uploadedByMe) {
      params.uploaded_by_me = "true";
    }

    if (versionState) {
      params.version_state = versionState;
    }

    // Server-side as well (#1601): documents, images or videos only.
    if (mediaType) {
      params.media_type = mediaType;
    }

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents`,
      {
        method: "GET",
        params,
      }
    );

    return response.data as CdbTqsDocumentListResponse;
  },

  /**
   * The first `count` documents of the list, fetched page by page.
   *
   * TQS serves at most DOCUMENT_PAGE_SIZE rows per request (cdb_tqs#1382), so a
   * caller that needs more — the Multimedia app's whole area, the Knowledge
   * Extractor's refresh of everything already loaded — pages through instead
   * of asking for one large page. Stops early when the list runs out.
   */
  async getDocumentsUpTo(
    count: number,
    query: {
      knowledgeArea?: string
      statusFilter?: DocumentStatus | readonly DocumentStatus[]
      search?: string
      sortBy?: DocumentSortKey
      sortDir?: DocumentSortDirection
      uploadedByMe?: boolean
      mediaType?: DocumentMediaType
      versionState?: DocumentVersionState
    } = {}
  ): Promise<CdbTqsDocumentListResponse> {
    const documents: CdbTqsDocumentListResponse["documents"] = [];
    let total = 0;
    while (documents.length < count) {
      const page = await documentsApi.getDocuments(
        query.knowledgeArea,
        Math.min(DOCUMENT_PAGE_SIZE, count - documents.length),
        documents.length,
        query.statusFilter,
        query.search,
        query.sortBy,
        query.sortDir,
        query.uploadedByMe,
        query.mediaType,
        query.versionState
      );
      total = page.total;
      documents.push(...page.documents);
      if (page.documents.length === 0 || documents.length >= total) break;
    }
    return { documents, total, limit: documents.length, offset: 0 };
  },

  /**
   * Download a document file and create a blob URL
   * @param documentId - Document UUID
   * @param filename - Original filename for download
   * @returns Blob URL for the document file
   */
  async getDocumentFile(
    documentId: string,
    filename: string,
    signal?: AbortSignal,
    /** Refuse (FileTooLargeForBrowserError) instead of holding more than this in memory. */
    options: { maxBytes?: number } = {},
  ): Promise<string> {
    const url = `${CDB_TQS_API_BASE_URL}/documents/${documentId}/file`;

    logApiCall("GET", url);

    try {
      // Get auth headers from the authService session (Azure AD)
      const authHeaders = await getAuthHeaders();

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/octet-stream",
          ...authHeaders, // Add JWT token
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to download document: ${response.status} ${response.statusText}`
        );
      }

      logApiResponse("GET", url, response.status, { filename });

      const blob = options.maxBytes
        ? await readBlobCapped(response, options.maxBytes)
        : await response.blob();

      // Ensure correct MIME type based on filename extension
      const mimeType = getMimeTypeFromFilename(filename);
      const typedBlob = mimeType && blob.type !== mimeType
        ? new Blob([blob], { type: mimeType })
        : blob;

      return URL.createObjectURL(typedBlob);
    } catch (error) {
      logError(error as Error, {
        method: "GET",
        url,
        context: "Document file download failed",
      });
      throw error;
    }
  },

  /**
   * Render a corporate document via the preview endpoint and create a blob URL.
   * DOCX/PPTX arrive Collabora-converted to PDF (see `preview_document` in
   * cdb_tqs); PDF/image formats stream as-is. Unlike `getDocumentFile`, this
   * does NOT relabel the blob to the original filename's MIME type — for
   * converted formats that would be actively wrong (the bytes are a PDF).
   * @param documentId - Document UUID
   * @param filename - Original filename (only used for logging/error context)
   * @returns Blob URL for the previewable content
   */
  async getDocumentPreview(documentId: string, filename: string, signal?: AbortSignal): Promise<string> {
    const url = `${CDB_TQS_API_BASE_URL}/documents/${documentId}/preview`;

    logApiCall("GET", url);

    try {
      const authHeaders = await getAuthHeaders();

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { ...authHeaders },
        signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to load document preview: ${response.status} ${response.statusText}`
        );
      }

      logApiResponse("GET", url, response.status, { filename });

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      logError(error as Error, {
        method: "GET",
        url,
        context: "Document preview failed",
      });
      throw error;
    }
  },

  /**
   * Start knowledge extraction for a document
   * @param documentId - Document UUID
   * @returns Extraction job information
   */
  async startExtraction(documentId: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}/extract`,
      {
        method: "POST",
      }
    );

    return response.data;
  },

  /**
   * Get extraction status for a document
   * @param documentId - Document UUID
   * @returns Extraction status information
   */
  async getExtractionStatus(documentId: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}/extraction-status`,
      {
        method: "GET",
      }
    );

    return response.data;
  },

  /**
   * Get knowledge graph data for a document
   * @param documentId - Document UUID
   * @returns Knowledge graph nodes and edges
   */
  async getKnowledgeGraph(documentId: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}/knowledge-graph`,
      {
        method: "GET",
      }
    );

    return response.data;
  },

  /**
   * Upload a new document
   * @param file - File to upload
   * @param title - Document title
   * @param knowledgeArea - Knowledge area name
   * @param origin - Who wrote the content: `human` (default, a person supplied
   *   it) or `ai_generated` (the system produced it, e.g. a Communicator
   *   research report). Persisted on the document and surfaced on the
   *   retrieval reference, so a later answer can tell a primary source from one
   *   we wrote earlier (cdb_tqs#1027). Omitted → the backend default `human`,
   *   which is what every hand-uploaded document means.
   * @param drive - The Drive file this upload comes from (cdb_tqs#1515). The
   *   server links the document to it, so `GET /personal-files/brain-status`
   *   can report the processing state later and on any device.
   * @returns Job response with job_id
   */
  async uploadDocument(
    file: File,
    title: string,
    knowledgeArea: string,
    clientId: string,
    autoVerifyTl1?: boolean,
    origin?: 'human' | 'ai_generated',
    drive?: UploadDriveLink,
    signal?: AbortSignal,
    // Appended AFTER drive/signal on purpose: those two already have call sites
    // and tests passing them positionally (cdb_tqs#1515), so taking position 7
    // here would silently shift them. Only knowledge-upload.ts passes this one.
    audioRecordingId?: string,
    // The document this upload is the new version of (cdb_tqs#1974); last for
    // the same reason as audioRecordingId.
    versionOf?: string,
    // What of a recording this upload files (cdb_tqs#2480); last for the same reason.
    audioFiledContent?: 'summary' | 'transcript' | 'both',
  ): Promise<any> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', title)
    formData.append('knowledge_area', knowledgeArea)
    formData.append('client_id', clientId)
    if (autoVerifyTl1 !== undefined) {
      formData.append('auto_verify_tl1', String(autoVerifyTl1))
    }
    if (origin) {
      formData.append('origin', origin)
    }
    if (drive) {
      formData.append('drive_file_id', drive.fileId)
      if (drive.version !== undefined && drive.version >= 1) {
        formData.append('drive_version', String(drive.version))
      }
    }
    // Sent with the upload, not after it: the backend stamps the recording in
    // this same request, so there is no second call to forget (cdb_tqs#1392).
    if (audioRecordingId) {
      formData.append('audio_recording_id', audioRecordingId)
      if (audioFiledContent) formData.append('audio_filed_content', audioFiledContent)
    }
    if (versionOf) {
      formData.append('version_of', versionOf)
    }

    const url = `${CDB_TQS_API_BASE_URL}/documents/upload`;

    logApiCall('POST', url, { filename: file.name, title, knowledgeArea, clientId, autoVerifyTl1, origin, audioRecordingId })

    try {
      // Get auth headers (JWT token)
      const authHeaders = await getAuthHeaders();

      const response = await fetch(url, {
        method: "POST",
        credentials: 'include',
        headers: authHeaders, // Add JWT token for authentication
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
        signal,
      });

      const data = response.headers
        .get("content-type")
        ?.includes("application/json")
        ? await response.json()
        : await response.text();

      logApiResponse("POST", url, response.status, data);

      if (!response.ok) {
        // Before the duplicate check: "identical to the current version" is a
        // 409 too, but names the version rather than a duplicate.
        const versionError = parseVersionError(response.status, data)
        if (versionError) throw versionError
        if (response.status === 409) {
          const detail = typeof data === 'object' && data !== null ? data.detail : null
          throw new DuplicateDocumentError(
            (detail && typeof detail === 'object' && detail.message) ||
              'This file already exists in the system',
            (detail && typeof detail === 'object' && detail.existing_document) || null,
          )
        }
        if (response.status === 429) {
          const detail = typeof data === 'object' && data !== null ? data.detail : null
          throw new UploadRateLimitError(
            typeof detail === 'string' ? detail : 'Upload rate limit exceeded',
            parseRetryAfterMs(response.headers.get('retry-after')),
          )
        }
        if (response.status === 415) {
          const detail = parseUnsupportedFormatDetail(data)
          if (detail) throw new UnsupportedFormatError(detail)
        }
        if (response.status === 404) {
          const detail = typeof data === 'object' && data !== null ? data.detail : null
          if (detail && typeof detail === 'object' && detail.code === 'drive_file_not_found') {
            throw new DriveFileNotFoundError(
              typeof detail.message === 'string' ? detail.message : 'Drive file not found',
            )
          }
        }
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`
        );
      }

      return data;
    } catch (error) {
      logError(error as Error, {
        method: "POST",
        url,
        context: "Document upload failed",
      });
      throw error;
    }
  },

  /**
   * Get a single document by ID
   * @param documentId - Document UUID
   * @returns Document details
   */
  async getDocument(documentId: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}`,
      {
        method: "GET",
      }
    );

    return response.data;
  },

  /**
   * Update document metadata
   * @param documentId - Document UUID
   * @param data - Partial document data to update
   * @returns Updated document
   */
  async updateDocument(
    documentId: string,
    data: { title?: string; knowledge_area?: string }
  ): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );

    return response.data;
  },

  /**
   * Delete a document
   * @param documentId - Document UUID
   */
  async deleteDocument(documentId: string): Promise<void> {
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/documents/${documentId}`, {
      method: "DELETE",
    });
  },

  /**
   * Reprocess a document
   * @param documentId - Document UUID
   * @returns Job response with job_id
   */
  async reprocessDocument(documentId: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}/reprocess`,
      {
        method: "POST",
      }
    );

    return response.data;
  },

  /**
   * Get document content as text
   * @param documentId - Document UUID
   * @returns Document content
   */
  async getDocumentContent(documentId: string): Promise<string> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}/content`,
      {
        method: "GET",
      }
    );

    return response.data.content || response.data;
  },

  /**
   * Get raw markdown for a document
   * @param documentId - Document UUID
   * @returns Raw markdown content
   */
  async getRawMarkdown(documentId: string): Promise<string> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}/markdown/raw`,
      {
        method: "GET",
      }
    );

    return response.data.content || response.data;
  },

  /**
   * Get cleaned markdown for a document
   * @param documentId - Document UUID
   * @returns Cleaned markdown content
   */
  async getCleanedMarkdown(documentId: string): Promise<string> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}/markdown/cleaned`,
      {
        method: "GET",
      }
    );

    return response.data.content || response.data;
  },

  /**
   * Get all chunks for a document
   * @param documentId - Document UUID
   * @returns Document chunks with metadata
   */
  async getDocumentChunks(documentId: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/documents/${documentId}/chunks`,
      {
        method: "GET",
      }
    );

    return response.data;
  },

  /**
   * Get the URL for a document image (served via backend proxy from private storage)
   * @param documentId - Document UUID
   * @param imageFilename - Image filename (e.g. "picture_000.png")
   * @returns URL to the image endpoint
   */
  getDocumentImageUrl(documentId: string, imageFilename: string): string {
    return `${CDB_TQS_API_BASE_URL}/documents/${documentId}/images/${imageFilename}`;
  },

  /**
   * Fetch a document image as a blob URL (with auth headers)
   * @param documentId - Document UUID
   * @param imageFilename - Image filename (e.g. "picture_000.png")
   * @returns Blob URL for the image, or null on failure
   */
  async getDocumentImageBlobUrl(documentId: string, imageFilename: string): Promise<string | null> {
    const url = `${CDB_TQS_API_BASE_URL}/documents/${documentId}/images/${imageFilename}`;
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(url, {
        method: "GET",
        credentials: 'include',
        headers: {
          Accept: "image/png",
          ...authHeaders,
        },
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  },

  /**
   * Thumbnail of an image document (cdb_tqs#1515): a small JPEG without EXIF.
   * Sent with `Cache-Control: private` and an ETag, so the browser cache
   * answers repeat requests; the caller keeps the object URL.
   * @param size - Longest edge in px: 160, 320 or 640
   * @returns The JPEG, or null when the document has none (not an image,
   *   unreadable original, or a backend without the endpoint)
   */
  async getDocumentThumbnail(
    documentId: string,
    size: 160 | 320 | 640,
    signal?: AbortSignal,
  ): Promise<Blob | null> {
    const url = `${CDB_TQS_API_BASE_URL}/documents/${documentId}/thumbnail?size=${size}`;
    const doFetch = (headers: Record<string, string>) =>
      fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "image/jpeg", ...headers },
        signal,
      });
    let response = await doFetch(await getAuthHeaders());
    // A token that expired while the chat was open: refresh and retry once,
    // mirroring fetchWithLogging, instead of leaving the icon for good.
    if (response.status === 401) {
      const { authService } = await import("@/services/auth-service");
      const newToken = await authService.refreshAccessToken();
      if (newToken) response = await doFetch({ Authorization: `Bearer ${newToken}` });
    }
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`Thumbnail failed: ${response.status} ${response.statusText}`);
    }
    return response.blob();
  },

  /**
   * File types a Knowledge Area upload can currently process (cdb_tqs#1515).
   * Throws when the backend does not offer the route yet — callers fall back
   * to the fixed list then.
   */
  async getIngestFormats(): Promise<IngestFormats> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/documents/formats`, {
      method: "GET",
    });
    const formats = parseIngestFormats(response.data);
    if (!formats) throw new Error("Unexpected /documents/formats response");
    return formats;
  },

  /**
   * Get job status by job ID
   * @param jobId - Job UUID
   * @returns Job status with progress details
   */
  async getJobStatus(jobId: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/jobs/${jobId}`,
      {
        method: "GET",
      }
    );

    return response.data;
  },

  /**
   * Transcribe audio to text using Whisper
   * @param audioBlob - Audio recording blob
   * @param language - Language code (ISO 639-1)
   * @returns Transcribed text and detected language
   */
  async transcribeAudio(
    audioBlob: Blob,
    language: string = "de"
  ): Promise<{ text: string; language: string }> {
    const formData = new FormData();
    formData.append("audio", audioBlob, "recording.webm");
    formData.append("language", language);

    // Get auth headers from the authService session (Azure AD)
    const authHeaders = await getAuthHeaders();

    const response = await fetch(`${CDB_TQS_API_BASE_URL}/agent/transcribe`, {
      method: "POST",
      credentials: 'include',
      headers: {
        ...authHeaders, // Add JWT token
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Transcription failed: ${response.statusText}`);
    }

    return await response.json();
  },
};
